import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.variantSelected, this.updateSelectedVariantPrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.variantSelected, this.updateSelectedVariantPrice);
  }

  /**
   * Updates the displayed price from the selected option's rendered money strings.
   * This keeps the product page and quick add modal on a single variant price even
   * when section rendering returns placeholder/range markup.
   * @param {Event} event
   */
  updateSelectedVariantPrice = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.dataset.productId !== this.dataset.productId) return;
    if (event.detail?.resource?.hasCompleteSelection === false) return;

    this.#renderSelectedVariantPrice(event.target);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    if (event.target instanceof HTMLElement) {
      this.#renderSelectedVariantPrice(event.target);
    }
  };

  /**
   * @param {HTMLElement} variantPicker
   */
  #renderSelectedVariantPrice(variantPicker) {
    const selectedOption = this.#getSelectedOption(variantPicker);
    const variantPrice = selectedOption?.dataset.variantPrice;

    if (!variantPrice) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]');
    if (!priceContainer) return;

    const compareAtPrice = selectedOption?.dataset.variantCompareAtPrice;
    priceContainer.innerHTML = '';

    const price = document.createElement('span');
    price.className = 'price';
    price.textContent = variantPrice;

    if (compareAtPrice) {
      const compareAtGroup = document.createElement('span');
      compareAtGroup.setAttribute('role', 'group');

      const hiddenLabel = document.createElement('span');
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = 'Regular price ';

      const compareAt = document.createElement('span');
      compareAt.className = 'compare-at-price';
      compareAt.textContent = compareAtPrice;

      compareAtGroup.append(hiddenLabel, compareAt);
      priceContainer.append(price, compareAtGroup);
      return;
    }

    priceContainer.append(price);
  }

  /**
   * @param {HTMLElement} variantPicker
   * @returns {HTMLOptionElement | HTMLInputElement | undefined}
   */
  #getSelectedOption(variantPicker) {
    const selectedSelectOption = Array.from(variantPicker.querySelectorAll('select.variant-option__select'))
      .filter((select) => select instanceof HTMLSelectElement && select.value !== '')
      .map((select) => (select instanceof HTMLSelectElement ? select.selectedOptions[0] : undefined))
      .find((option) => option instanceof HTMLOptionElement);

    const selectedOption = selectedSelectOption || variantPicker.querySelector('fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
